/**
 * In/out pattern anomaly detection for the attendance timeline.
 *
 * A correctly-tracked day alternates: entry → exit → entry → exit. When the AI module
 * misses a face — the person walked past at a bad angle, or the crop scored below the
 * match threshold — the sighting is still captured, but as an *unknown* face rather than
 * as this identity. The person's own timeline then shows two entries in a row with no
 * exit between them, and their inside-duration silently under- or over-counts.
 *
 * We can't recover the missing event here, but we can point at exactly where it should
 * have been, so an operator can open the face log for that window and link the unknown
 * face to the right person. Once linked, the sighting is re-attributed and the pattern
 * repairs itself on the next load.
 *
 * Two things break alternation:
 *   - the day's first real event is an exit  → the entry was never recorded
 *   - two entries or two exits in a row      → the event between them was never recorded
 *
 * `unknown` entries (neither in nor out) are skipped rather than treated as breaks: they
 * carry no direction, so they can't tell us anything about alternation either way.
 */

export type AnomalyKind = 'day_starts_with_exit' | 'repeated_entry' | 'repeated_exit';

export interface PatternAnomaly {
  /**
   * Index into the row's `pattern` / `events` arrays of the event that breaks
   * alternation. The two are built in the same pass server-side (one push per event),
   * so a single index addresses both.
   */
  index: number;
  kind: AnomalyKind;
  /** The direction the system never received — what should sit before `index`. */
  missing: 'in' | 'out';
  /**
   * Index of the previous in/out event, i.e. the other side of the gap. Null when the
   * day opens on an exit, where the gap has no lower bound in this day's data.
   */
  afterIndex: number | null;
}

/** True for the two directions that participate in alternation. */
const isDirectional = (io: string): io is 'in' | 'out' => io === 'in' || io === 'out';

/**
 * Find every point where the in/out sequence breaks alternation.
 * Returns anomalies in chronological order; an empty array means a clean day.
 */
export function detectPatternAnomalies(pattern: string[]): PatternAnomaly[] {
  const anomalies: PatternAnomaly[] = [];
  let prev: { io: 'in' | 'out'; index: number } | null = null;

  for (let i = 0; i < pattern.length; i++) {
    const io = pattern[i];
    if (!isDirectional(io)) continue;

    if (!prev) {
      // First directional event of the day. Leaving without ever arriving means the
      // entry was missed — the one case where the gap has no lower bound.
      if (io === 'out') {
        anomalies.push({ index: i, kind: 'day_starts_with_exit', missing: 'in', afterIndex: null });
      }
    } else if (io === prev.io) {
      anomalies.push({
        index: i,
        kind: io === 'in' ? 'repeated_entry' : 'repeated_exit',
        missing: io === 'in' ? 'out' : 'in',
        afterIndex: prev.index,
      });
    }

    prev = { io, index: i };
  }

  return anomalies;
}

/** Indices that should render as an anomaly dot, for O(1) lookup while mapping. */
export function anomalyIndexMap(anomalies: PatternAnomaly[]): Map<number, PatternAnomaly> {
  return new Map(anomalies.map((a) => [a.index, a]));
}

/** Human phrasing for a dot tooltip / timeline placeholder heading. */
export function anomalyLabel(a: PatternAnomaly): string {
  switch (a.kind) {
    case 'day_starts_with_exit':
      return 'Day starts with an exit — the entry was never recorded';
    case 'repeated_entry':
      return 'Second entry in a row — the exit between them was never recorded';
    case 'repeated_exit':
      return 'Second exit in a row — the entry between them was never recorded';
  }
}

/** "Missing entry" / "Missing exit" — the placeholder's title in the expanded view. */
export function missingLabel(missing: 'in' | 'out'): string {
  return missing === 'in' ? 'Missing entry' : 'Missing exit';
}
